import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, HTMLAttributes } from 'react';
import { motion, useAnimation, Variants, Transition } from 'framer-motion';

export interface ChevronUpIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

export interface ChevronUpIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  isHovered?: boolean;
}

const DEFAULT_TRANSITION: Transition = {
  times: [0, 0.4, 1],
  duration: 0.5,
};

const ChevronUpIcon = forwardRef<ChevronUpIconHandle, ChevronUpIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 28, isHovered, ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseEnter?.(e);
        } else {
          controls.start("animate");
        }
      },
      [controls, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseLeave?.(e);
        } else {
          controls.start("normal");
        }
      },
      [controls, onMouseLeave]
    );

    useEffect(() => {
      if (isHovered !== undefined) {
        if (isHovered) {
          if (ref && 'current' in ref && ref.current && typeof (ref.current as any).startAnimation === 'function') {
            (ref.current as any).startAnimation();
          } else {
            handleMouseEnter({} as React.MouseEvent<HTMLDivElement>);
          }
        } else {
          if (ref && 'current' in ref && ref.current && typeof (ref.current as any).stopAnimation === 'function') {
            (ref.current as any).stopAnimation();
          } else {
            handleMouseLeave({} as React.MouseEvent<HTMLDivElement>);
          }
        }
      }
    }, [isHovered, handleMouseEnter, handleMouseLeave, ref]);


    return (
      <div
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.path
            animate={controls}
            d="m18 15-6-6-6 6"
            transition={DEFAULT_TRANSITION}
            variants={{
              normal: { y: 0 },
              animate: { y: [0, -2, 0] },
            }}
          />
        </svg>
      </div>
    );
  }
);

ChevronUpIcon.displayName = "ChevronUpIcon";

export { ChevronUpIcon };
